import React, { useEffect, useMemo, useState } from 'react';
import { ConfidenceStrip } from '../../components/journeys/ConfidenceStrip';
import { FunnelChart } from '../../components/journeys/FunnelChart';
import { InsightPanel } from '../../components/journeys/InsightPanel';
import { JourneyFilterBar } from '../../components/journeys/JourneyFilterBar';
import { JourneyKpiRow } from '../../components/journeys/JourneyKpiRow';
import { JourneyPageHeader } from '../../components/journeys/JourneyPageHeader';
import { ModeSwitcher } from '../../components/journeys/ModeSwitcher';
import { PendingCard } from '../../components/journeys/PendingCard';
import { StepTable } from '../../components/journeys/StepTable';
import { PanelStateMessage } from '../../components/dashboard/PanelStateMessage';
import {
  fetchFunnel,
  fetchJourneyFilters,
  fetchMembershipFunnel,
  fetchSalesChannelFunnel,
} from '../../api/journeys';
import { useAuth } from '../../context/AuthContext';
import { useTenantView } from '../../context/TenantViewContext';
import {
  FUNNEL_MOCK_BY_MODE,
  FUNNEL_MODES,
  JOURNEY_FILTER_OPTIONS,
} from '../../data/journeysMock';
import { usePersistedState } from '../../hooks/usePersistedState';
import { buildJourneyApiFilters, LIVE_DATA_SOURCE_LABEL } from '../../lib/journeyApiFilters';
import {
  mapFunnelApiToView,
  mapMembershipFunnelApiToView,
  mapSalesChannelFunnelApiToView,
} from '../../lib/mapFunnelApi';

const DEFAULT_FILTERS = {
  season: 'all',
  venue: 'all',
  event: 'all',
  channel: 'all',
};

const FUNNEL_PREFS_KEY = 'vi.funnel.prefs.v1';
const DEFAULT_PREFS = {
  mode: 'attendance',
  filters: DEFAULT_FILTERS,
};

const ALL_VENUE = { value: 'all', label: 'Venue: all' };
const ALL_EVENT = { value: 'all', label: 'Event: all' };

function mapFilterOptions(items, kind) {
  return (items || []).map((item) => ({
    value: String(item.value),
    label:
      kind === 'venue'
        ? `Venue: ${item.label}`
        : `Event: ${item.label}`,
  }));
}

export function FunnelPage() {
  const { isSuperAdmin } = useAuth();
  const { viewingTenant } = useTenantView();
  const accountId = isSuperAdmin && viewingTenant?.id ? viewingTenant.id : null;

  const [prefs, setPrefs] = usePersistedState(FUNNEL_PREFS_KEY, DEFAULT_PREFS);
  const mode = FUNNEL_MODES.some((item) => item.id === prefs?.mode)
    ? prefs.mode
    : 'attendance';
  const appliedFilters = { ...DEFAULT_FILTERS, ...(prefs?.filters || {}) };
  const [draftFilters, setDraftFilters] = useState(appliedFilters);
  const [selectedStageId, setSelectedStageId] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usingMock, setUsingMock] = useState(false);
  const [venueOptions, setVenueOptions] = useState([ALL_VENUE, ...JOURNEY_FILTER_OPTIONS.venues.slice(1)]);
  const [eventOptions, setEventOptions] = useState([ALL_EVENT, ...JOURNEY_FILTER_OPTIONS.events.slice(1)]);

  const setMode = (nextMode) => {
    setPrefs((prev) => ({ ...DEFAULT_PREFS, ...prev, mode: nextMode }));
  };

  const setAppliedFilters = (nextFilters) => {
    const resolved = typeof nextFilters === 'function' ? nextFilters(appliedFilters) : nextFilters;
    setPrefs((prev) => ({ ...DEFAULT_PREFS, ...prev, filters: resolved }));
  };

  const channelDisabled = mode !== 'sales_channel';

  const filterFields = useMemo(
    () => [
      {
        key: 'season',
        label: 'Season',
        options: [
          { value: 'all', label: 'All seeded dates' },
          ...JOURNEY_FILTER_OPTIONS.seasons,
        ],
      },
      { key: 'venue', label: 'Venue', options: venueOptions },
      {
        key: 'event',
        label: 'Event',
        options: eventOptions,
      },
      {
        key: 'channel',
        label: 'Channel',
        options: JOURNEY_FILTER_OPTIONS.channels,
        disabled: channelDisabled,
      },
    ],
    [channelDisabled, venueOptions, eventOptions]
  );

  useEffect(() => {
    let cancelled = false;
    async function loadFilters() {
      try {
        const data = await fetchJourneyFilters({ accountId });
        if (cancelled) return;
        setVenueOptions([ALL_VENUE, ...mapFilterOptions(data.venues, 'venue')]);
        setEventOptions([ALL_EVENT, ...mapFilterOptions(data.events, 'event')]);
      } catch {
        if (cancelled) return;
        // Keep whatever options we already have (mock fallback).
      }
    }
    loadFilters();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const filters = buildJourneyApiFilters({
          season: appliedFilters.season,
          venue: appliedFilters.venue,
          event: appliedFilters.event,
          accountId,
        });

        let api;
        let mapped;
        if (mode === 'membership') {
          api = await fetchMembershipFunnel(filters);
          mapped = mapMembershipFunnelApiToView(api);
        } else if (mode === 'sales_channel') {
          api = await fetchSalesChannelFunnel(filters);
          mapped = mapSalesChannelFunnelApiToView(api);
        } else {
          api = await fetchFunnel(filters);
          mapped = mapFunnelApiToView(api);
        }

        if (cancelled) return;
        setLiveData(mapped);
        setUsingMock(false);
      } catch (err) {
        if (cancelled || err?.name === 'AbortError') return;
        setError(err?.message || 'Failed to load funnel');
        setLiveData(null);
        setUsingMock(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [
    mode,
    appliedFilters.season,
    appliedFilters.venue,
    appliedFilters.event,
    accountId,
  ]);

  const mockData = FUNNEL_MOCK_BY_MODE[mode] || FUNNEL_MOCK_BY_MODE.attendance;
  const data = liveData && !usingMock ? liveData : mockData;
  const activeInsight =
    (selectedStageId && data.stageInsights?.[selectedStageId]) || data.insight;

  function handleModeChange(nextMode) {
    setMode(nextMode);
    setSelectedStageId(null);
  }

  function handleFilterChange(key, value) {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleApply() {
    setAppliedFilters(draftFilters);
    setSelectedStageId(null);
  }

  function handleExport() {
    const payload = {
      mode,
      filters: appliedFilters,
      exportedAt: new Date().toISOString(),
      source: !usingMock ? 'api' : 'mock',
      kpis: data.kpis,
      stages: data.chart.stages,
      steps: data.steps,
      raw: data.raw || null,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `venue-intelligence-funnel-${mode}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const dataSourceLabel = !usingMock
    ? LIVE_DATA_SOURCE_LABEL
    : `Demo · mock fallback${error ? ` · ${error}` : ''}`;

  const loadingMessage =
    mode === 'membership'
      ? 'Reading membership & add-on stages from the API.'
      : mode === 'sales_channel'
        ? 'Reading sales-channel funnel from the API.'
        : 'Reading ticket → scan → POS stages from the API.';

  return (
    <section className="flex w-full min-w-0 flex-col gap-4">
      <JourneyPageHeader
        leading={
          <ModeSwitcher modes={FUNNEL_MODES} value={mode} onChange={handleModeChange} />
        }
        dataSourceLabel={dataSourceLabel}
        onExport={handleExport}
      />

      <ConfidenceStrip tone={data.confidence.tone} title={data.confidence.title} />

      <JourneyFilterBar
        fields={filterFields}
        values={draftFilters}
        onChange={handleFilterChange}
        onApply={handleApply}
      />

      {loading ? (
        <PanelStateMessage
          variant="loading"
          title="Loading funnel"
          message={loadingMessage}
        />
      ) : null}

      {!loading ? (
        <div className="grid min-w-0 items-start gap-4 xl:grid-cols-[minmax(0,1.75fr)_minmax(240px,0.8fr)]">
          <FunnelChart
            title={data.chart.title}
            stages={data.chart.stages}
            selectedStageId={selectedStageId}
            onSelectStage={setSelectedStageId}
          />
          <div className="flex min-w-0 flex-col gap-3">
            <InsightPanel
              eyebrow={activeInsight?.eyebrow}
              title={activeInsight?.title}
              action={activeInsight?.action}
            />
            {mode === 'attendance' && data.pending ? (
              <PendingCard total={data.pending.total} fixtures={data.pending.fixtures || []} />
            ) : null}
          </div>
        </div>
      ) : null}

      {!loading ? <JourneyKpiRow items={data.kpis} /> : null}

      {!loading ? (
        <StepTable
          title={`Step detail — ${FUNNEL_MODES.find((item) => item.id === mode)?.label || 'funnel'}`}
          rows={data.steps}
          defaultOpen={true}
        />
      ) : null}
    </section>
  );
}
